/**
 * MobilePreviewFrame - Mobile device frame and chrome for preview panel
 *
 * Single Responsibility: Renders the mobile device frame (notch, bezel, home indicator),
 * header bar with navigation and language toggle, and contains screen content.
 *
 * Does NOT handle: Screen-specific content rendering (podcasts list, detail, player, etc.).
 * Those are handled by PreviewScreens composables.
 */
package com.castly.admin.ui.preview

import androidx.compose.runtime.*
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

sealed class PreviewScreen {
    object Home : PreviewScreen()
    object Podcasts : PreviewScreen()
    data class PodcastDetail(val content: com.castly.admin.dto.ContentResponse) : PreviewScreen()
    data class Episodes(val content: com.castly.admin.dto.ContentResponse) : PreviewScreen()
    data class Player(val content: com.castly.admin.dto.ContentResponse, val episode: com.castly.admin.dto.EpisodeResponse) : PreviewScreen()

    // Audiobook screens (now unified with podcast screens using ContentResponse)
    object Audiobooks : PreviewScreen()
    data class AudiobookDetail(val content: com.castly.admin.dto.ContentResponse) : PreviewScreen()
    data class AudiobookEpisodes(val content: com.castly.admin.dto.ContentResponse) : PreviewScreen()
    data class AudiobookPlayer(val content: com.castly.admin.dto.ContentResponse, val episode: com.castly.admin.dto.EpisodeResponse) : PreviewScreen()

    // Engagement screens
    object MyLikes : PreviewScreen()
    object ContinueWatchingList : PreviewScreen()
}

@Composable
fun MobilePreviewFrame(
    isVisible: Boolean,
    currentScreen: PreviewScreen,
    selectedLanguage: String,
    currentUserId: String,
    onNavigateBack: () -> Unit,
    onToggleLanguage: () -> Unit,
    onChangeUserId: (String) -> Unit,
    onClose: () -> Unit,
    onTabChange: (PreviewScreen) -> Unit,
    screenContent: @Composable () -> Unit,
    miniPlayer: @Composable (() -> Unit)?
) {
    val colors = AppTheme.colors

    if (!isVisible) return

    // Phone frame container (side panel layout) - bezel: keep dark
    Div({
        style {
            width(420.px)
            minWidth(420.px)
            property("height", "calc(100vh - 64px)")
            backgroundColor(Color("#111827")) // bezel background - intentionally hardcoded
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            padding(Spacing.xl)
            property("box-sizing", "border-box")
        }
    }) {
        // Phone device frame - bezel: keep dark
        Div({
            style {
                width(375.px)
                property("max-height", "calc(100vh - 120px)")
                backgroundColor(Color("#1f2937")) // bezel frame - intentionally hardcoded
                borderRadius(40.px)
                padding(Spacing.md)
                property("box-shadow", "0 25px 50px -12px rgba(0, 0, 0, 0.5)")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                flex(1)
            }
        }) {
            // Phone notch - bezel: keep dark
            Div({
                style {
                    width(120.px)
                    height(28.px)
                    backgroundColor(Color("#1f2937")) // bezel notch - intentionally hardcoded
                    borderRadius(0.px, 0.px, 14.px, 14.px)
                    property("margin", "0 auto 8px auto")
                    position(Position.Relative)
                    property("z-index", "10")
                }
            }) {
                // Camera dot - bezel: keep dark
                Div({
                    style {
                        width(10.px)
                        height(10.px)
                        backgroundColor(Color("#374151")) // bezel camera - intentionally hardcoded
                        borderRadius(50.percent)
                        position(Position.Absolute)
                        left(50.percent)
                        top(50.percent)
                        property("transform", "translate(-50%, -50%)")
                    }
                })
            }

            // Phone screen
            Div({
                style {
                    flex(1)
                    backgroundColor(Color(colors.surface))
                    borderRadius(28.px)
                    overflow("hidden")
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                }
            }) {
                // Header bar
                Div({
                    style {
                        padding(Spacing.lg)
                        backgroundColor(Color("#7c3aed")) // app accent - intentionally hardcoded
                        color(Color("#ffffff"))
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.SpaceBetween)
                    }
                }) {
                    Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(Spacing.md) } }) {
                        // Back button (if not on main tabs)
                        if (currentScreen !is PreviewScreen.Home &&
                            currentScreen !is PreviewScreen.Podcasts &&
                            currentScreen !is PreviewScreen.Audiobooks &&
                            currentScreen !is PreviewScreen.MyLikes) {
                            Button({
                                onClick { onNavigateBack() }
                                style {
                                    backgroundColor(Color.transparent)
                                    border(0.px)
                                    color(Color("#ffffff"))
                                    cursor("pointer")
                                    fontSize(Typography.xl)
                                    padding(0.px)
                                }
                            }) { Text("<") }
                        }
                        Span({ style { fontWeight("600"); fontSize(Typography.lg) } }) {
                            Text(
                                when (currentScreen) {
                                    is PreviewScreen.Home -> "Castly"
                                    is PreviewScreen.Podcasts -> "Castly"
                                    is PreviewScreen.PodcastDetail -> currentScreen.content.title
                                    is PreviewScreen.Episodes -> "Episodes"
                                    is PreviewScreen.Player -> "Now Playing"
                                    is PreviewScreen.Audiobooks -> "Castly"
                                    is PreviewScreen.AudiobookDetail -> currentScreen.content.title
                                    is PreviewScreen.AudiobookEpisodes -> "Episodes"
                                    is PreviewScreen.AudiobookPlayer -> "Now Playing"
                                    is PreviewScreen.MyLikes -> "My Likes"
                                    is PreviewScreen.ContinueWatchingList -> "Continue Watching"
                                }
                            )
                        }
                    }
                    Div({ style { display(DisplayStyle.Flex); gap(Spacing.sm); alignItems(AlignItems.Center); position(Position.Relative) } }) {
                        // User switcher
                        PreviewUserSwitcher(
                            currentUserId = currentUserId,
                            onChangeUserId = onChangeUserId
                        )

                        // Language toggle button
                        Button({
                            onClick { onToggleLanguage() }
                            style {
                                backgroundColor(Color("#ffffff"))
                                color(Color("#7c3aed"))
                                border(0.px)
                                borderRadius(BorderRadius.sm)
                                cursor("pointer")
                                fontSize(Typography.sm)
                                fontWeight("600")
                                padding(Spacing.xs, 10.px)
                            }
                        }) { Text(selectedLanguage.uppercase()) }

                        // Close button
                        Button({
                            onClick { onClose() }
                            style {
                                backgroundColor(Color.transparent)
                                border(0.px)
                                color(Color("#ffffff"))
                                cursor("pointer")
                                fontSize(Typography.xl)
                                padding(Spacing.xs, Spacing.sm)
                            }
                        }) { Text("X") }
                    }
                }

                // Screen content
                Div({
                    style {
                        flex(1)
                        overflow("auto")
                        backgroundColor(Color(colors.surfaceHover))
                        property("-webkit-overflow-scrolling", "touch")
                        property("will-change", "scroll-position")
                    }
                }) {
                    screenContent()
                }

                // Mini player (if provided)
                miniPlayer?.invoke()

                // Bottom navigation tabs (show on main tab screens)
                if (currentScreen is PreviewScreen.Home ||
                    currentScreen is PreviewScreen.Podcasts ||
                    currentScreen is PreviewScreen.Audiobooks ||
                    currentScreen is PreviewScreen.MyLikes) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            backgroundColor(Color(colors.surface))
                            property("border-top", "1px solid ${colors.border}")
                        }
                    }) {
                        // Home tab
                        Button({
                            onClick { onTabChange(PreviewScreen.Home) }
                            style {
                                flex(1)
                                padding(Spacing.md, Spacing.lg)
                                backgroundColor(if (currentScreen is PreviewScreen.Home) Color(colors.surfaceHover) else Color.transparent)
                                border(0.px)
                                cursor("pointer")
                                display(DisplayStyle.Flex)
                                flexDirection(FlexDirection.Column)
                                alignItems(AlignItems.Center)
                                gap(Spacing.xs)
                                color(if (currentScreen is PreviewScreen.Home) Color("#7c3aed") else Color(colors.textMuted))
                            }
                        }) {
                            Div({ style { fontSize(Typography.xl) } }) { Text("🏠") }
                            Div({ style { fontSize(Typography.xs); fontWeight(if (currentScreen is PreviewScreen.Home) "600" else "400") } }) {
                                Text("Home")
                            }
                        }

                        // Podcasts tab
                        Button({
                            onClick { onTabChange(PreviewScreen.Podcasts) }
                            style {
                                flex(1)
                                padding(Spacing.md, Spacing.lg)
                                backgroundColor(if (currentScreen is PreviewScreen.Podcasts) Color(colors.surfaceHover) else Color.transparent)
                                border(0.px)
                                cursor("pointer")
                                display(DisplayStyle.Flex)
                                flexDirection(FlexDirection.Column)
                                alignItems(AlignItems.Center)
                                gap(Spacing.xs)
                                color(if (currentScreen is PreviewScreen.Podcasts) Color("#7c3aed") else Color(colors.textMuted))
                            }
                        }) {
                            Div({ style { fontSize(Typography.xl) } }) { Text("P") }
                            Div({ style { fontSize(Typography.xs); fontWeight(if (currentScreen is PreviewScreen.Podcasts) "600" else "400") } }) {
                                Text("Podcasts")
                            }
                        }

                        // Audiobooks tab
                        Button({
                            onClick { onTabChange(PreviewScreen.Audiobooks) }
                            style {
                                flex(1)
                                padding(Spacing.md, Spacing.lg)
                                backgroundColor(if (currentScreen is PreviewScreen.Audiobooks) Color(colors.surfaceHover) else Color.transparent)
                                border(0.px)
                                cursor("pointer")
                                display(DisplayStyle.Flex)
                                flexDirection(FlexDirection.Column)
                                alignItems(AlignItems.Center)
                                gap(Spacing.xs)
                                color(if (currentScreen is PreviewScreen.Audiobooks) Color("#7c3aed") else Color(colors.textMuted))
                            }
                        }) {
                            Div({ style { fontSize(Typography.xl) } }) { Text("A") }
                            Div({ style { fontSize(Typography.xs); fontWeight(if (currentScreen is PreviewScreen.Audiobooks) "600" else "400") } }) {
                                Text("Audiobooks")
                            }
                        }

                        // My Likes tab
                        Button({
                            onClick { onTabChange(PreviewScreen.MyLikes) }
                            style {
                                flex(1)
                                padding(Spacing.md, Spacing.lg)
                                backgroundColor(if (currentScreen is PreviewScreen.MyLikes) Color(colors.surfaceHover) else Color.transparent)
                                border(0.px)
                                cursor("pointer")
                                display(DisplayStyle.Flex)
                                flexDirection(FlexDirection.Column)
                                alignItems(AlignItems.Center)
                                gap(Spacing.xs)
                                color(if (currentScreen is PreviewScreen.MyLikes) Color("#7c3aed") else Color(colors.textMuted))
                            }
                        }) {
                            Div({ style { fontSize(Typography.xl) } }) { Text("\u2764") } // ❤
                            Div({ style { fontSize(Typography.xs); fontWeight(if (currentScreen is PreviewScreen.MyLikes) "600" else "400") } }) {
                                Text("Likes")
                            }
                        }
                    }
                }
            }

            // Home indicator - bezel: keep dark
            Div({
                style {
                    width(134.px)
                    height(5.px)
                    backgroundColor(Color("#4b5563")) // bezel indicator - intentionally hardcoded
                    borderRadius(3.px)
                    property("margin", "8px auto 0 auto")
                }
            })
        }
    }
}
